import { View } from "react-native";
import React, { useEffect, useRef, useState } from "react";
import { Radio, Text } from "@ui-kitten/components";
import StripePaymentForm, {
  StripeTokenError,
  StripeTokenResponse,
} from "components/StripePaymentForm/StripePaymentForm";
import { useAppSelector } from "hooks/useAppSelector";
import { isDev } from "config/env";
import toast from "utils/toast";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  createPrimaryUser,
  createShopPageAccount,
} from "features/slices/buyFlowSlice";
import useTenantUserFields from "hooks/useTenantUserFields";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useSendConversionData from "hooks/useSendConversionData";
import FullScreenSpinner from "components/FullScreenSpinner";
import { toDashboard } from "utils/navigationUtils";
import useAuth from "hooks/useAuth";
import { useMutation } from "@tanstack/react-query";
import { trackConversion } from "queries/conversion.queries";
import { planIdProperty } from "./helpers";
import { createContactFetcher } from "queries/user.queries";

const StepFour = () => {
  const dispatch = useAppDispatch();
  const { selectedPlan, quantity, signupCharges } = useAppSelector(
    (state) => state.shopPagePhaseTwo
  );
  const auth = useAuth();
  const {
    selectedBillingInterval,
    loading,
    isEditingPrimaryUser,
    primaryUser,
  } = useAppSelector((state) => state.buyFlow);
  const [userCreated, setUserCreated] = useState(false);
  const contactSubmitted = useRef(false); // Ref that keeps track of whether contact has been submitted, to prevent multiple submissions

  const sendConversionData = useSendConversionData();
  const { mutate: track } = useMutation(trackConversion);
  const { mutateAsync: createContact } = useMutation(createContactFetcher);

  const { tenantUserFields } = useTenantUserFields();
  const { planPackageUserFields } = usePlanPackageUserFields(
    selectedPlan?.[planIdProperty]
  );

  const planPackageSignupCharge = signupCharges.find(
    (charge) =>
      charge.billingInterval === selectedBillingInterval &&
      charge.planPackageId === selectedPlan?.[planIdProperty]
  );

  const handleTokenReceived = async (
    error: StripeTokenError | undefined,
    response?: StripeTokenResponse
  ) => {
    if (error) {
      toast.error(error.error.message);
      return;
    }
    let userCreateResult;
    // meaning user is making a checkout attempt the first time
    if (!userCreated) {
      // create primary user via /user/register endpoint
      userCreateResult = await dispatch(
        createPrimaryUser({
          tenantUserFields: tenantUserFields!,
          planPackageUserFields: planPackageUserFields!,
        })
      ).unwrap();
      setUserCreated(true);
    }
    // create account
    await dispatch(
      createShopPageAccount({
        billingToken: response!,
        quantity,
        planPackageId: selectedPlan![planIdProperty]!,
        tenantUserFields: tenantUserFields!,
        planPackageUserFields: planPackageUserFields!,
      })
    );
    sendConversionData(
      planPackageSignupCharge!.signupCharge,
      quantity,
      response!
    );
    track({
      step: "PURCHASE_COMPLETE",
      flow: "SHOP_PHASE_TWO",
      userId: userCreated ? primaryUser.id : userCreateResult?.id,
    });
  };

  // After user purchases a plan, redirect to dashboard
  useEffect(() => {
    if (!auth.isLoggedIn) return;
    if (auth.isLoggedIn && userCreated) {
      toDashboard();
    }
  }, [auth, userCreated]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (primaryUser.email && contactSubmitted.current === false) {
          contactSubmitted.current = true;
          createContact({
            firstName: primaryUser.firstName,
            lastName: primaryUser.lastName,
            phone: primaryUser.phone,
            email: primaryUser.email,
            source: "SHOP_PAGE_PHASE_TWO",
            planPackageId: selectedPlan![planIdProperty]!,
            buyflowStep: "Payment Information",
          });
        }
      }
    };

    window.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [selectedPlan, primaryUser]);

  return (
    <View>
      {loading && <FullScreenSpinner text="Creating your account..." />}
      <Text category="h6">Payment</Text>
      <View className="bg-white rounded-xl my-4 shadow-lg font-semibold text-lg">
        <View className="border-b-2 border-b-stone-200">
          <View className="p-4">
            <Radio checked>Credit Card</Radio>
          </View>
        </View>
        <View className="p-4">
          <StripePaymentForm
            onTokenReceived={handleTokenReceived}
            disabled={isEditingPrimaryUser}
          />
        </View>
      </View>
    </View>
  );
};

export default StepFour;
